/**
 * The on-device recognizer behind R13's live text (owner decision
 * 2026-10-13: Vosk, offline — no audio leaves the browser).
 *
 * `vosk-browser` is Kaldi compiled to WebAssembly, running in its own
 * worker. It is ~6 MB of JavaScript, so it is imported only when a practice
 * that repeats is opened, never with the app (see vite.config.js). The model
 * (`vosk-model-small-en-us`, ~40 MB) is downloaded once by that worker and
 * kept in the browser's IndexedDB under its URL, so the URL is versioned in
 * the file name and must stay stable between visits — a blob URL would
 * defeat the cache.
 *
 * Every recognizer is built with a grammar: the sentence's own words
 * (`spokenWords`) plus `[unk]`. The small model then only decides which of
 * those words was said, which is what makes it usable for accented speech —
 * and the live text can only ever "type" the sentence's words, the effect the
 * owner asked for.
 */

/** Versioned on purpose: the worker caches the model under this exact string. */
export const VOSK_MODEL_URL = import.meta.env.VITE_VOSK_MODEL_URL || '/nest-media/models/vosk-model-small-en-us-0.15.tar.gz'

/** About how much the first download is, for the learner's notice. */
export const VOSK_MODEL_MEGABYTES = 40

/** Audio is handed to the worker in batches of about this many seconds (the worklet's quanta are ~3 ms). */
const BATCH_SECONDS = 0.1

/** How long `finish` waits for the worker's final result before it settles for the last partial. */
const FINAL_TIMEOUT_MS = 2500

/** Can this browser run the recognizer at all? Without it, repetitions are counted by voice alone. */
export function liveTextSupported(scope = globalThis) {
  return typeof scope.WebAssembly === 'object' && typeof scope.Worker === 'function' && typeof scope.AudioWorkletNode === 'function'
}

/** The recognizer's text (`"i would like"`) as words, `[unk]` kept — the caller decides what to show. */
export function wordsOf(text) {
  return String(text ?? '').split(/\s+/).filter(Boolean)
}

/** A grammar for `KaldiRecognizer`: the sentence's words, each once, plus `[unk]`. */
export function grammarFor(words) {
  return JSON.stringify([...new Set(words), '[unk]'])
}

let modelPromise = null

/**
 * The shared model, loaded once per page. Resolves to null — never throws —
 * when it cannot be had (offline on the first visit, the file not on the
 * server, a worker that dies), so a practice falls back to counting by voice.
 */
export function loadModel({ importVosk = () => import('vosk-browser'), url = VOSK_MODEL_URL } = {}) {
  if (!modelPromise) {
    modelPromise = (async () => {
      try {
        const Vosk = await importVosk()
        const model = await Vosk.createModel(url)
        return model
      } catch (error) {
        console.warn('[live text] model unavailable:', error)
        modelPromise = null // try again on the next practice
        return null
      }
    })()
  }
  return modelPromise
}

/** For tests: forget the loaded model. */
export function resetModel() {
  modelPromise = null
}

/**
 * One repetition's recognizer. `feed(chunk, sampleRate)` with the
 * microphone's Float32 quanta as they come; `onWords(words)` is called with
 * the words heard so far whenever they change; `finish()` resolves to the
 * final words once the take has stopped. `close()` frees the worker side.
 */
export function createRecognizer(model, { words, sampleRate, onWords = () => {} }) {
  const recognizer = new model.KaldiRecognizer(sampleRate, grammarFor(words))
  let settled = []
  let lastShown = ''
  let resolveFinal = null
  let pending = []
  let pendingLength = 0
  const batch = Math.round(sampleRate * BATCH_SECONDS)

  const show = (heard) => {
    const key = heard.join(' ')
    if (key === lastShown) return
    lastShown = key
    onWords(heard)
  }

  recognizer.on('partialresult', (message) => {
    show([...settled, ...wordsOf(message.result?.partial)])
  })
  recognizer.on('result', (message) => {
    // A pause inside the take ends an utterance: its words are final and the
    // next partial starts empty, so they are kept here and shown before it.
    settled = [...settled, ...wordsOf(message.result?.text)]
    show(settled)
    if (resolveFinal) resolveFinal(settled)
  })
  recognizer.on('error', (message) => {
    console.warn('[live text] recognizer error:', message.error)
    if (resolveFinal) resolveFinal(lastShown ? lastShown.split(' ') : settled)
  })

  const flush = () => {
    if (pendingLength === 0) return
    const buffer = new Float32Array(pendingLength)
    let offset = 0
    for (const chunk of pending) {
      buffer.set(chunk, offset)
      offset += chunk.length
    }
    pending = []
    pendingLength = 0
    recognizer.acceptWaveformFloat(buffer, sampleRate)
  }

  return {
    feed(chunk) {
      pending.push(chunk)
      pendingLength += chunk.length
      if (pendingLength >= batch) flush()
    },
    finish() {
      flush()
      return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(lastShown ? lastShown.split(' ') : settled), FINAL_TIMEOUT_MS)
        resolveFinal = (heard) => {
          clearTimeout(timer)
          resolveFinal = null
          resolve(heard)
        }
        recognizer.retrieveFinalResult()
      })
    },
    close() {
      try {
        recognizer.remove()
      } catch {
        // The worker is gone already; nothing to free.
      }
    },
  }
}
